import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, LogOut } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { OrderCard } from "@/components/admin/OrderCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });
const FILTERS = ["all", "new", "confirmed", "delivered", "cancelled"];

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).join(" ") : "Something went wrong. Please try again.");

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      onDone(data);
    } catch (err) {
      setError(errText(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const input = "w-full border-0 border-b border-cream/30 bg-transparent py-3 text-base text-cream outline-none placeholder:text-stone2/70 focus:border-cream";
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="mx-auto mt-16 w-full max-w-md space-y-8 border border-cream/20 bg-ink2 p-8">
      <h1 className="font-display text-4xl font-light">Order <span className="italic text-white">inbox</span></h1>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="admin-email-input" className={input} />
      <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="admin-password-input" className={input} />
      {error && <p data-testid="admin-login-error" className="text-sm text-[#5a0f12]">{error}</p>}
      <button type="submit" disabled={busy} data-testid="admin-login-button"
        className="flex w-full items-center justify-center gap-2 rounded-full bg-cream py-4 font-mono text-xs uppercase tracking-[0.3em] text-white disabled:opacity-60">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
      </button>
    </form>
  );
};

const Inbox = ({ onLogout }) => {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    http.get("/admin/orders").then((r) => setOrders(Array.isArray(r.data) ? r.data : [])).catch(() => setOrders([]));
  }, []);
  const setStatus = async (id, status) => {
    try {
      const { data } = await http.patch(`/admin/orders/${id}`, { status });
      setOrders((list) => list.map((o) => (o.id === id ? { ...o, status: data?.status ?? status } : o)));
      toast.success(`Order marked ${status}`);
    } catch {
      toast.error("Couldn’t update the order");
    }
  };
  const shown = (orders ?? []).filter((o) => filter === "all" || o.status === filter);
  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-6 border-b border-cream/15 pb-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em]">Private</p>
          <h1 className="mt-2 font-display text-4xl font-light sm:text-5xl">Order <span className="italic text-white">inbox</span></h1>
        </div>
        <button onClick={onLogout} data-testid="admin-logout-button" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] hover:text-white">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`admin-filter-${f}`}
            className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${filter === f ? "border-cream bg-cream text-white" : "border-cream/30 hover:border-cream"}`}>
            {f} {f === "all" ? `(${orders?.length ?? 0})` : `(${(orders ?? []).filter((o) => o.status === f).length})`}
          </button>
        ))}
      </div>
      {orders === null ? <Loader2 className="mt-10 h-6 w-6 animate-spin" /> : (
        <div className="mt-8 space-y-4" data-testid="admin-orders-list">
          {shown.length === 0 && <p data-testid="admin-orders-empty" className="text-stone2">No orders here yet.</p>}
          {shown.map((o) => <OrderCard key={o.id} order={o} onStatus={setStatus} />)}
        </div>
      )}
    </div>
  );
};

export default function AdminPage() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const logout = async () => {
    await http.post("/auth/logout").catch(() => null);
    setUser(false);
  };
  return (
    <div className="min-h-screen bg-ink px-5 pb-24 font-body text-cream sm:px-10">
      <header className="py-5"><Link to="/" data-testid="admin-home-logo"><Logo /></Link></header>
      {user === null && <Loader2 className="mx-auto mt-20 h-6 w-6 animate-spin" />}
      {user === false && <Login onDone={setUser} />}
      {user && <Inbox onLogout={logout} />}
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}
